import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import Dashboard from './pages/Dashboard';
import WeeklyMonthlyReport from './pages/WeeklyMonthlyReport';
import SubstratesReconciliation from './pages/SubstratesReconciliation';
import ChemicalLabReport from './pages/ChemicalLabReport';
import TSSDigestateForm from './pages/TSSDigestateForm';
import TSDigestateForm from './pages/TSDigestateForm';
import VSDigestateForm from './pages/VSDigestateForm';
import SubstrateTSForm from './pages/SubstrateTSForm';
import SubstrateVSForm from './pages/SubstrateVSForm';
import NitrateNForm from './pages/NitrateNForm';
import PhosphateForm from './pages/PhosphateForm';
import GasMonitoring from './pages/GasMonitoring';
import FostacMonitoring from './pages/FostacMonitoring';
import DailyBiogasMonitoring from './pages/DailyBiogasMonitoring';
import MonthlyBillingReport from './pages/MonthlyBillingReport';
import FlaredGasReport from './pages/FlaredGasReport';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Register from './pages/Register';
import { colors, layout } from './theme/designSystem';
import { WebSocketProvider } from './context/WebSocketContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

const MainContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const getActiveMenu = () => {
    const path = location.pathname;
    if (path === '/weekly-monthly-report') return 'weeklyMonthlyReport';
    if (path === '/substrates') return 'substrates';
    if (path.startsWith('/lab-report')) return 'labReport';
    if (path.startsWith('/gas-monitor')) return 'gasMonitor';
    if (path.startsWith('/fostac')) return 'fostac';
    if (path.startsWith('/daily-biogas')) return 'dailyBiogas';
    if (path.startsWith('/monthly-billing')) return 'monthlyBilling';
    if (path.startsWith('/flared-gas-report')) return 'flaredGasReport';
    if (path === '/settings') return 'settings';
    return 'dashboard';
  };
  
  const activeMenu = getActiveMenu();

  const handleMenuClick = (menuId: string) => {
    const routes: Record<string, string> = {
      dashboard: '/',
      weeklyMonthlyReport: '/weekly-monthly-report',
      substrates: '/substrates',
      labReport: '/lab-report',
      gasMonitor: '/gas-monitor',
      fostac: '/fostac',
      dailyBiogas: '/daily-biogas',
      monthlyBilling: '/monthly-billing',
      flaredGasReport: '/flared-gas-report',
      settings: '/settings',
    };
    navigate(routes[menuId] || '/');
  };

  const getPageTitle = () => {
    const titles: Record<string, string> = {
      dashboard: 'Dashboard',
      weeklyMonthlyReport: 'Weekly / Monthly Report',
      substrates: 'Substrates Reconciliation',
      labReport: location.pathname === '/lab-report/tss'
        ? 'TSS Digestate'
        : location.pathname === '/lab-report/ts'
        ? 'TS Digestate'
        : location.pathname === '/lab-report/vs'
        ? 'VS Digestate'
        : location.pathname === '/lab-report/substrate-ts'
        ? 'Substrate %TS'
        : location.pathname === '/lab-report/substrate-vs'
        ? 'Substrate %VS'
        : location.pathname === '/lab-report/nitrate-n'
        ? 'Nitrate-Nitrogen'
        : location.pathname === '/lab-report/phosphate' && location.search.includes('mode=reactive')
        ? 'Phosphate Reactive'
        : location.pathname === '/lab-report/phosphate' && location.search.includes('mode=total')
        ? 'Phosphate Total'
        : location.pathname === '/lab-report/phosphate'
        ? 'Phosphate (Reactive & Total)'
        : 'Chemical Lab Report',
      gasMonitor: 'Gas Monitoring',
      fostac: 'FOSTAC & Gas Quality Monitoring',
      dailyBiogas: 'Daily Biogas Usage Monitoring',
      monthlyBilling: 'Monthly Billing Report',
      flaredGasReport: 'Flared Gas Report',
      settings: 'Settings',
    };
    return titles[activeMenu] || 'Dashboard';
  };

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Box sx={{ display: 'flex', backgroundColor: colors.neutral[50], minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar 
        activeItem={activeMenu} 
        onMenuClick={(menuId) => {
          handleMenuClick(menuId);
          if (isMobile) setMobileOpen(false);
        }}
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerToggle}
        isMobile={isMobile}
      />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          marginLeft: { xs: 0, md: layout.sidebar.width },
          minHeight: '100vh',
          width: { xs: '100%', md: `calc(100% - ${layout.sidebar.width})` },
        }}
      >
        {/* Top Bar */}
        <TopBar 
          title={getPageTitle()} 
          onMenuClick={isMobile ? handleDrawerToggle : undefined}
          isMobile={isMobile}
        />

        {/* Content Area */}
        <Box
          sx={{
            marginTop: layout.topBar.height,
            padding: { xs: '16px', sm: '20px', md: layout.contentPadding },
          }}
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/weekly-monthly-report" element={<WeeklyMonthlyReport />} />
            <Route path="/substrates" element={<SubstratesReconciliation />} />
            <Route path="/lab-report" element={<ChemicalLabReport />} />
            <Route path="/lab-report/tss" element={<TSSDigestateForm />} />
            <Route path="/lab-report/ts" element={<TSDigestateForm />} />
            <Route path="/lab-report/vs" element={<VSDigestateForm />} />
            <Route path="/lab-report/substrate-ts" element={<SubstrateTSForm />} />
            <Route path="/lab-report/substrate-vs" element={<SubstrateVSForm />} />
            <Route path="/lab-report/nitrate-n" element={<NitrateNForm />} />
            <Route path="/lab-report/phosphate" element={<PhosphateForm />} />
            <Route path="/gas-monitor" element={<GasMonitoring />} />
            <Route path="/fostac" element={<FostacMonitoring />} />
            <Route path="/daily-biogas" element={<DailyBiogasMonitoring />} />
            <Route path="/monthly-billing" element={<MonthlyBillingReport />} />
            <Route path="/flared-gas-report" element={<FlaredGasReport />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <WebSocketProvider>
          <MainContent />
        </WebSocketProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
